import React from "react";
import { Link } from "react-router-dom";
import { CASES } from "@/data/cases";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, Quote, TrendingUp, MapPin, Clock } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";

export default function Cases() {
  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />

      <PublicHeader active="cases" />

      <section className="relative z-10 max-w-5xl mx-auto px-6 pt-20 pb-16">
        <p className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Cases de sucesso</p>
        <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight">
          Resultados reais de varejistas brasileiros.
        </h1>
        <p className="text-muted-foreground text-lg mt-5 max-w-2xl leading-relaxed">
          Veja como lojas de diferentes setores transformaram a gestão de estoque com SmartStockAI — com números, fotos e depoimentos reais.
        </p>
      </section>

      <section className="relative z-10 max-w-6xl mx-auto px-6 pb-24 space-y-10">
        {CASES.map((c, i) => (
          <article key={c.id} data-testid={`case-${c.id}`} className="rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/40 transition">
            <div className="grid lg:grid-cols-[1.2fr_1fr] gap-0">
              {/* Story side */}
              <div className="p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-border">
                <div className="flex items-center gap-3 mb-5 flex-wrap">
                  <span className={`text-xs px-2.5 py-1 rounded-md border font-mono ${c.color}`}>{c.sector}</span>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="w-3 h-3" /> {c.location}</span>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="w-3 h-3" /> {c.period}</span>
                </div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-2">{c.company}</h2>
                <p className="font-display text-xl text-primary mb-6">{c.headline}</p>

                <div className="space-y-4 text-sm">
                  <div>
                    <div className="font-semibold mb-1">Desafio</div>
                    <p className="text-muted-foreground leading-relaxed">{c.challenge}</p>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Solução</div>
                    <p className="text-muted-foreground leading-relaxed">{c.solution}</p>
                  </div>
                </div>

                <blockquote className="mt-7 p-5 rounded-xl bg-secondary/40 border border-border relative">
                  <Quote className="w-6 h-6 text-primary/30 absolute -top-2 left-4" />
                  <p className="text-base italic leading-relaxed mb-3">"{c.quote}"</p>
                  <div className="text-xs text-muted-foreground">— {c.author}</div>
                </blockquote>
              </div>

              {/* Results side */}
              <div className="p-8 lg:p-10 bg-gradient-to-br from-primary/5 to-transparent">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-primary mb-5">
                  <TrendingUp className="w-3.5 h-3.5" /> Resultados em {c.period}
                </div>
                <div className="space-y-3">
                  {c.results.map((r) => (
                    <div key={r.metric} className="p-4 rounded-xl border border-border bg-card">
                      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{r.metric}</div>
                      <div className="grid grid-cols-3 gap-3 items-baseline">
                        <div>
                          <div className="text-[10px] uppercase text-muted-foreground">antes</div>
                          <div className="font-mono text-sm">{r.before}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-muted-foreground">depois</div>
                          <div className="font-mono text-sm font-semibold">{r.after}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] uppercase text-muted-foreground">delta</div>
                          <div className="font-mono text-primary font-semibold">{r.delta}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </article>
        ))}
      </section>

      <section className="relative z-10 max-w-5xl mx-auto px-6 py-16">
        <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-12 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Pronto para criar seu próprio case?</h2>
          <p className="text-muted-foreground mt-4">Comece grátis hoje. Vejamos seu nome aqui em 90 dias.</p>
          <Link to="/signup">
            <Button size="lg" className="mt-7 bg-primary hover:bg-primary/90 h-12 px-8">
              Começar grátis <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="relative z-10 border-t border-border/50 py-8 text-center text-sm text-muted-foreground">
        © 2026 SmartStockAI · Inteligência preditiva para o varejo
      </footer>
    </div>
  );
}
